import React from 'react'
import cx from 'classnames'
import Collapse from '@kunukn/react-collapse'
import css from 'styled-jsx/css'
import dayjs from 'dayjs'
import { Deal } from '../types'
import Link from 'next/link'
import DealsHeader from './DealsHeader'
import { useAuthContext } from '../context/Auth'
import { useRouter } from 'next/router'

type DealsProps = {
  showActiveBorder?: boolean
  deals: Array<Deal>
  isFilterable: boolean
  showHeader?: boolean
  isCompleted: boolean
}

function Deals(props: DealsProps) {
  const router = useRouter()
  const authContext = useAuthContext()
  const { deals, showActiveBorder, isCompleted, showHeader, isFilterable } =
    props
  const [activeIdx, setActiveIdx] = React.useState<number[]>([])

  const handleDealClick = (index: number, deal: Deal) => {
    const isOpen = activeIdx.includes(index)
    setActiveIdx((prevState) => isOpen ? prevState.filter((e) => e !== index) : [...prevState, index] )

    if(!isOpen) {
      authContext.onActivityEvent( 'click', { path: `${router.asPath}/${deal.slug}`, query: router.query } )
    }
  }

  return (
    <div className="border-bottom">
      {showHeader && (
        <DealsHeader isFilterable={isFilterable} isCompleted={isCompleted} />
      )}
      {deals.map((deal, index) => {
        const isOpen = activeIdx.includes(index)

        const locations = deal?.locations
          ?.filter((location) => location.name)
          .map((location) => {
            let name = `${location.name}${location.hq ? ' (HQ)' : ''}`
            return name
          })

        return (
          <div
            key={`deal-item-${index}`}
            className={cx('text-xs', {
              'bg-white-100': isOpen,
            })}
          >
            <button onClick={() => handleDealClick(index, deal)}
              className={cx('deal-row hover:bg-white-100', {
                'bg-white': !isOpen,
                'deal-row--active': isOpen && showActiveBorder,
                '!border-t-0': index === 0,
                completed: isCompleted,
              })}
            >
              <div className="text-left lg:my-auto">
                <span className="font-semibold lg:hidden">Seller:</span>
                <p className="mt-1 lg:mt-0 lg:font-bold">
                  {deal.sellers.length
                    ? deal.sellers
                        ?.map((e) => e?.name)
                        .filter(Boolean)
                        .join(', ')
                    : ''}
                </p>
              </div>
              {deal.status?.parent?.name === 'Completed' && (
                <div className="text-left lg:my-auto">
                  <span className="font-semibold lg:hidden">Buyer:</span>
                  <p className="mt-1 lg:mt-0 lg:font-bold">
                    {deal.buyer?.name}
                  </p>
                </div>
              )}
              <div className="text-left lg:my-auto lg:text-center">
                <span className="font-semibold lg:hidden">Asset name:</span>
                <p className="mt-1 lg:mt-0">{deal.title}</p>
              </div>
              <div className="col-span-2 text-left md:col-span-1 lg:my-auto lg:text-center">
                <span className="font-semibold lg:hidden">Asset type:</span>
                <p className="mt-1 lg:mt-0">{deal.assets?.labels?.type}</p>
              </div>
              <div className="text-left lg:my-auto lg:text-center">
                <span className="font-semibold lg:hidden">Advisor:</span>
                <p className="mt-1 lg:mt-0">
                  {deal.sellside?.financial?.length
                    ? deal.sellside?.financial
                        .map((e) => e?.company?.name)
                        .filter(Boolean)
                        .join(', ')
                    : ''}
                </p>
              </div>
              <div className="text-left lg:my-auto lg:text-right">
                <span className="font-semibold lg:hidden">Launch date:</span>
                <p className="mt-1 lg:mt-0">
                  {deal.dates?.launched
                    ? dayjs(deal.dates.launched).format('MMM YYYY')
                    : ''}
                </p>
              </div>
            </button>
            <Collapse
              isOpen={isOpen}
              className={cx(
                'collapse-container',
                { 'collapse-container--active': isOpen },
                components.className
              )}
            >
              <div className="mt-6 mb-12 px-6 lg:mt-8">
                <span className="font-semibold">Objective:</span>{' '}
                <p className="mt-1 lg:mt-0 lg:inline-block">{deal.objective}</p>
              </div>
              <div
                className={cx(
                  'collapse--content',
                  deal.status?.parent?.name === 'Completed'
                    ? 'completed'
                    : 'no-completed',
                  components.className
                )}
              >
                <div>
                  <span className="font-semibold">Location:</span>
                  <p className="mt-1 lg:mt-2">
                    {locations?.length ? locations.join(', ') : ''}
                  </p>
                </div>
                {deal.status?.parent?.name === 'Completed' &&
                  (deal.price || 0) > 0 && (
                    <div>
                      <span className="font-semibold">Price:</span>
                      <p className="mt-1 lg:mt-2">${deal.price}</p>
                    </div>
                  )}
                <div>
                  <span className="font-semibold">Asset size:</span>
                  <p className="mt-1 lg:mt-2">{deal.assets?.labels?.size}</p>
                </div>
                <div>
                  {deal.sellside?.codename && (
                    <>
                      <span className="font-semibold">Code name:</span>
                      <p className="mt-1 lg:mt-2">{deal.sellside?.codename}</p>
                    </>
                  )}
                </div>
                <div>
                  {deal?.contract && (
                    <>
                      <span className="font-semibold">Contract / Hedge:</span>
                      <p className="mt-1 lg:mt-2">{deal?.contract}</p>
                    </>
                  )}
                </div>
                <div>
                  <span className="font-semibold">
                    Financial Advisor
                    {deal.status?.parent?.name === 'Completed'
                      ? ' (Sellside)'
                      : ''}
                    :
                  </span>
                  <div className="mt-1 lg:mt-2">
                    {deal.sellside?.financial?.map((f, i) => (
                      <div
                        className={cx(i > 0 && 'mt-3')}
                        key={`financial-advisor-sellside-${i}-${index}`}
                      >
                        <p>
                          {f.company?.name}
                          {i === 0 ? ' (Lead)' : ''}
                        </p>
                        {f.linkedin_profile_url ? (
                          <a
                            href={f.linkedin_profile_url}
                            target="_blank"
                            className="inline-flex items-center underline hover:no-underline"
                          >
                            {f?.contact?.name}
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              className="ml-1"
                            >
                              <path d="M21 13v10h-21v-19h12v2h-10v15h17v-8h2zm3-12h-10.988l4.035 4-6.977 7.07 2.828 2.828 6.977-7.07 4.125 4.172v-11z" />
                            </svg>
                          </a>
                        ) : (
                          <p>{f.contact?.name}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {deal.status?.parent?.name === 'Completed' && (
                  <div>
                    <span className="font-semibold">
                      Financial Advisor (Buyside)
                    </span>
                    <div className="mt-1 lg:mt-2">
                      {deal.buyside?.financial?.map((f, i) => (
                        <div
                          className={cx(i > 0 && 'mt-3')}
                          key={`financial-advisor-buyside-${i}-${index}`}
                        >
                          <p>{f.company?.name}</p>
                          {f.linkedin_profile_url ? (
                            <a
                              href={f.linkedin_profile_url}
                              target="_blank"
                              className="inline-flex items-center underline hover:no-underline"
                            >
                              {f.contact?.name}
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                className="ml-1"
                              >
                                <path d="M21 13v10h-21v-19h12v2h-10v15h17v-8h2zm3-12h-10.988l4.035 4-6.977 7.07 2.828 2.828 6.977-7.07 4.125 4.172v-11z" />
                              </svg>
                            </a>
                          ) : (
                            <p>{f.contact?.name}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <span className="font-semibold">Energy Market(s):</span>
                  <p className="mt-1 lg:mt-2">
                    {deal.markets?.length
                      ? deal?.markets
                          .map((m) => m?.name)
                          .filter(Boolean)
                          .join(', ')
                      : ''}
                  </p>
                </div>
                <div>
                  <span className="font-semibold">
                    Key{' '}
                    {[
                      deal.dates?.launched,
                      deal.dates?.closed,
                      deal.dates?.announced,
                    ].filter(Boolean).length === 1
                      ? 'Date'
                      : 'Dates'}
                    :
                  </span>
                  <div className="mt-1 lg:mt-2">
                    {deal.dates?.launched && (
                      <p>
                        Launch: {dayjs(deal?.dates.launched).format('MMM YYYY')}
                      </p>
                    )}
                    {deal.dates?.closed && (
                      <p>
                        Financial Close:{' '}
                        {dayjs(deal?.dates.closed).format('MMM DD, YYYY')}
                      </p>
                    )}
                    {deal.dates?.announced && (
                      <p>
                        Deal Announced:{' '}
                        {dayjs(deal?.dates.announced).format('MMM DD, YYYY')}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  {deal.related && deal.related?.length > 0 && (
                    <>
                      <span className="font-semibold">Related News:</span>
                      <div className="mb-5 mt-1 lg:mt-2">
                        {deal?.related?.map((r, i) => (
                          <div className={cx(i > 0 && 'mt-3')} key={r.slug}>
                            <Link href={`/articles/${r.slug}`}>
                              <a className="font-medium underline hover:no-underline">
                                {r?.title}
                              </a>
                            </Link>
                            <p className="mt-1 text-xs">
                              {dayjs(r.date).format('MMM DD, YYYY')}
                            </p>
                          </div>
                        ))}
                      </div>
                      <Link href="/">
                        <a className="font-medium underline hover:no-underline">
                          View more articles
                        </a>
                      </Link>
                    </>
                  )}
                </div>
                {deal.status?.parent?.name === 'Completed' && (
                  <>
                    <div>
                      <span className="font-semibold">
                        Legal Advisor (Sellside):
                      </span>
                      <div className="mt-1 lg:mt-2">
                        {deal.sellside?.legal?.map((f, i) => (
                          <div
                            className={cx(i > 0 && 'mt-3')}
                            key={`legal-advisor-sellside-${i}-${index}`}
                          >
                            <p>{f?.company?.name}</p>
                            <p className="underline">{f.contact?.name}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className="font-semibold">
                        Legal Advisor (Buyside):
                      </span>
                      <div className="mt-1 lg:mt-2">
                        {deal.buyside?.legal?.map((f, i) => (
                          <div
                            className={cx(i > 0 && 'mt-3')}
                            key={`legal-advisor-sellside-${i}-${index}`}
                          >
                            <p>{f?.company?.name}</p>
                            <p className="underline">{f.contact?.name}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
              {components.styles}
            </Collapse>
          </div>
        )
      })}
      <style jsx>{`
        .deal-row {
          @apply grid w-full grid-cols-2 gap-12 px-6 py-6 text-center font-inter transition-colors duration-300 ease-in-out;
          min-height: 70px;
          border-top: dashed 1px black;
        }

        .border-bottom {
          border-bottom: dashed 1px black;
        }

        .deal-row--active {
          border-bottom: 1px solid #c8c8c8;
        }

        @screen md {
          .deal-row {
            @apply grid-cols-3;
          }
        }
        @screen lg {
          .deal-row {
            @apply w-full grid-cols-5 py-2;
          }
          .deal-row.completed {
            @apply grid-cols-6;
          }
        }
      `}</style>
    </div>
  )
}

const components = css.resolve`
  .collapse-container {
    @apply w-full;
    transition: height 300ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  .collapse-container--active:after {
    opacity: 0;
  }

  .collapse--content {
    @apply grid grid-cols-2 gap-10 px-6 pb-7 font-inter text-xs;
    transition: 300ms;
  }

  @screen md {
    .collapse--content {
      @apply grid-cols-4 gap-6;
    }
  }

  @screen lg {
    .collapse--content {
      @apply gap-12;
    }
    .collapse--content.no-completed {
      @apply grid-cols-4;
    }
    .collapse--content.completed {
      @apply grid-cols-5;
    }
  }
`

export default Deals
